import React, { useState, useEffect, useRef } from "react";
import { cloneDeep, capitalize } from "lodash";
import { useAssessments } from "../../utils/assessment";
import { AppProvider } from "../AppContext";
import { BLANK_CONTACT } from "../../utils/contact";
import { getMarketByLocale } from "../../utils/locales";
import { getCookieValue } from "../../utils/cookies";
import t from "../../utils/translate";
import addZendeskScript from "../../utils/addZendeskScript";
import {
  METRIC,
  IMPERIAL,
  getAlternateSystem,
  preferredMeasurementSystem,
  measurementUnitsForMarketAndSystem as getUnitsFromMeasurementSystem
} from "../../utils/measurementSystem";
import {
  GENDER,
  HEIGHT,
  WEIGHT,
  DESIRED_WEIGHT,
  WEIGHT_LOSS,
  MEASUREMENT_SYSTEM,
  WEIGHT_UNIT,
  HEIGHT_UNIT,
  FIRST_NAME,
  LAST_NAME,
  MEDICAL,
  STATE,
  PANCREATITIS_CAUSE,
  PANCREATITIS_CAUSE_UNKNOWN,
  PANCREATITIS_YEAR,
  GALLBLADDER_YEAR,
  GALLBLADDER_EXPERIENCE,
  ALLERGEN_MEDICATION,
  BLOOD_PRESSURE_UNKNOWN,
  BLOOD_PRESSURE_SYS,
  BLOOD_PRESSURE_DIA,
  HEART_RATE,
  DOB,
  EMAIL,
  PHONE_NUMBER,
  MAILING_ADDRESS,
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
  CITY,
  ZIPCODE,
  COUNTRY,
  US,
  LOGGED_IN_AS,
  OPTIN
} from "../Profile/constants";
import { Experiment, useXS } from "@ww-digital/xs-sdk-react";
import useLoggedInDataHandler from "../../utils/useLoggedInDataHandler";
import { setPageData } from "../../utils/analytics";
import useUrlSlugContext from "../UrlSlugContext";
import { isUniTestUrl } from "../UrlValidator";
import { loadResultsCookie } from "../Results/storeResultsData";
import {
  getConfigByLocale,
  SLUG_CLINICAL,
  SLUG_ELI,
  SLUG_UNI1,
  SLUG_UNI2,
  SLUG_UNI3,
  SLUG_UNI4
} from "../../config/global";

function useAppConfig({ urlSlug, coreUrlParam, eliUrlParam }) {
  const cookie = loadResultsCookie();
  const cookieResultsScreenSequence = cookie?.resultsScreenSequence;

  if (
    urlSlug === SLUG_UNI1 ||
    urlSlug === SLUG_UNI2 ||
    urlSlug === SLUG_UNI3 ||
    urlSlug === SLUG_UNI4
  ) {
    const slugMap = {
      [SLUG_UNI1]: "uni1",
      [SLUG_UNI2]: "uni2",
      [SLUG_UNI3]: "uni3",
      [SLUG_UNI4]: "uni4"
    };
    const dbSlugs = getConfigByLocale(window.ww.locale)[slugMap[urlSlug]][
      "dbSlugs"
    ];
    return {
      theme: "refresh",
      isUni: true,
      isUni1: urlSlug === SLUG_UNI1,
      isUni2: urlSlug === SLUG_UNI2,
      isUni3: urlSlug === SLUG_UNI3,
      isUni4: urlSlug === SLUG_UNI4,
      eli: false,
      isSequence: false,
      isSequenceResultsScreen: false,
      dbSlugs,
      coreUrl: null,
      eliUrl: null
    };
  }

  if (urlSlug === SLUG_ELI) {
    const dbSlugs = getConfigByLocale(window.ww.locale).eli.dbSlugs;

    return {
      theme: "refresh",
      isUni: false,
      isUni1: false,
      isUni2: false,
      eli: true,
      isSequence: false,
      isSequenceResultsScreen: false,
      dbSlugs,
      coreUrl: coreUrlParam,
      eliUrl: eliUrlParam
    };
  }

  if (urlSlug === SLUG_CLINICAL) {
    const dbSlugs = getConfigByLocale(window.ww.locale).isSequence.dbSlugs;
    const results = getCookieValue("ww_results", true);

    return {
      theme: "sequence",
      isUni: false,
      isUni1: false,
      isUni2: false,
      eli: false,
      isSequence: true,
      isSequenceResultsScreen: results?.shown
        ? cookieResultsScreenSequence
        : false,
      dbSlugs,
      coreUrl: null,
      eliUrl: null
    };
  }

  return {
    theme: "refresh",
    isUni: false,
    isUni1: false,
    isUni2: false,
    eli: false,
    isSequence: false,
    isSequenceResultsScreen: false,
    dbSlugs: getConfigByLocale(window.ww.locale).default.dbSlugs,
    coreUrl: null,
    eliUrl: null
  };
}

const getBlankProfile = (isSequence, isEli, isUni, isUniNew) => {
  const BLANK_PROFILE = {
    [FIRST_NAME]: null,
    [LAST_NAME]: null,
    [GENDER]: null,
    [MEASUREMENT_SYSTEM]: null,
    [METRIC]: {
      [HEIGHT]: null,
      [WEIGHT]: null,
      [WEIGHT_UNIT]: null,
      [HEIGHT_UNIT]: null,
      [DESIRED_WEIGHT]: null,
      [WEIGHT_LOSS]: null
    },
    [IMPERIAL]: {
      [HEIGHT]: null,
      [WEIGHT]: null,
      [WEIGHT_UNIT]: null,
      [HEIGHT_UNIT]: null,
      [DESIRED_WEIGHT]: null,
      [WEIGHT_LOSS]: null
    },
    ...((isEli || isSequence || isUni) && {
      [MEDICAL]: {
        [PANCREATITIS_CAUSE]: null,
        [PANCREATITIS_CAUSE_UNKNOWN]: null,
        [PANCREATITIS_YEAR]: null,
        [GALLBLADDER_EXPERIENCE]: null,
        [GALLBLADDER_YEAR]: null,
        [ALLERGEN_MEDICATION]: null,
        [BLOOD_PRESSURE_SYS]: null,
        [BLOOD_PRESSURE_DIA]: null,
        [BLOOD_PRESSURE_UNKNOWN]: null,
        [HEART_RATE]: null,
        [DOB]: null,
        [STATE]: null,
        [EMAIL]: null
      }
    }),
    ...((isSequence || isUniNew) && {
      [PHONE_NUMBER]: null,
      [MAILING_ADDRESS]: {
        [ADDRESS_LINE_1]: null,
        [ADDRESS_LINE_2]: null,
        [CITY]: null,
        [STATE]: null,
        [ZIPCODE]: null,
        [COUNTRY]: US
      },
      [OPTIN]: true
    })
  };

  const market = getMarketByLocale(window.ww.locale);
  const usedSystem = preferredMeasurementSystem({ market });
  const altSystem = getAlternateSystem(usedSystem);
  const { weight: weightUnits, height: heightUnits } =
    getUnitsFromMeasurementSystem(market, usedSystem);

  const { weight: altWeightUnits, height: altHeightUnits } =
    getUnitsFromMeasurementSystem(market, altSystem);

  BLANK_PROFILE[MEASUREMENT_SYSTEM] = usedSystem;
  BLANK_PROFILE[usedSystem][WEIGHT_UNIT] = weightUnits;
  BLANK_PROFILE[usedSystem][HEIGHT_UNIT] = heightUnits;
  BLANK_PROFILE[altSystem][WEIGHT_UNIT] = altWeightUnits;
  BLANK_PROFILE[altSystem][HEIGHT_UNIT] = altHeightUnits;

  return BLANK_PROFILE;
};

const getBlankAuthData = () => ({
  [LOGGED_IN_AS]: ""
});

// Save UTM code to session storage.
const crmInit = () => {
  if (window.location.href.includes("utm_campaign")) {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const utmParameter = url.searchParams.get("utm_campaign");
    let sessionItemName;
    if (utmParameter.includes("recruit-quiz-cta-crm-of")) {
      sessionItemName = "crmUserOf";
    }
    if (
      utmParameter.includes("crm-recruit") ||
      utmParameter.includes("recruit-assessment-cta-crm")
    ) {
      sessionItemName = "crmUser";
    }
    sessionStorage.setItem(sessionItemName, utmParameter);
  }
};

const isValidAssessment = assessment => {
  if (!Array.isArray(assessment.questions)) {
    return false;
  }

  const urlPaths = [];
  let isValid = true;

  assessment.questions.forEach(question => {
    question.answers.presentationMetadata.forEach(answer => {
      if (!answer.nextQuestionId) {
        console.error(
          `Some answer in question "${question.headerText1}" doesn't have next question set.`
        );
        isValid = false;
      } else if (
        answer.nextQuestionId !== "END" &&
        !assessment.questions.find(q => q._id === answer.nextQuestionId)
      ) {
        console.error(
          `Next question set in some answer of question "${question.headerText1}" was not found.`
        );
        isValid = false;
      } else if (answer.returnQuestionId && answer.nextQuestionId === "END") {
        console.error(
          `Not valid combination - comeback question set but next question is END in some answer of question "${question.headerText1}"`
        );
        isValid = false;
      } else if (
        answer.returnQuestionId &&
        assessment.questions.find(q => q._id === answer.nextQuestionId)[
          "slug"
        ] === question.slug
      ) {
        console.error(
          `Not valid combination - comeback question set but next question is not pointed to another slug in some answer of question "${question.headerText1}"`
        );
        isValid = false;
      }
    });

    if (!question.urlPath) {
      console.error(
        `Question "${question.headerText1}" doesn't have URL path set.`
      );
      isValid = false;
    } else {
      if (urlPaths.includes(question.urlPath)) {
        console.error(
          `Question "${question.headerText1}" has a duplicate URL path.`
        );
        isValid = false;
      } else {
        urlPaths.push(question.urlPath);
      }
    }
  });

  ["results", "email"].forEach(notAllowedPath => {
    if (urlPaths.includes(notAllowedPath)) {
      console.error("Some question has not valid URL path.");
      isValid = false;
    }
  });

  return isValid;
};

const checkIfAndroid = () => {
  const uagent = window.navigator.userAgent.toLowerCase();

  if (uagent.includes("android")) {
    return true;
  }

  return false;
};

const checkIfIphone = () => {
  const uagent = window.navigator.userAgent.toLowerCase();

  if (uagent.includes("iphone")) {
    return true;
  }

  return false;
};

function updateQuestionsForEligibility(clonedAssessment) {
  let questions = clonedAssessment.questions;
  const secondLast = questions[questions.length - 2];

  const editedQuestions = questions.map((question, idx) => {
    const newSectionTotal = question.sectionTotal - 1;

    // update questions with matching section as last question
    if (questions.length - 1 === idx) {
      return {
        ...question,
        sectionIndex: question.sectionIndex + 1,
        sectionTotal: question.sectionTotal - 1,
        newQuizSectionsInfo: {
          questionNumber: secondLast.newQuizSectionsInfo.questionNumber + 2,
          totalQuestionsInSection:
            secondLast.newQuizSectionsInfo.totalQuestionsInSection + 1
        }
      };
    } else {
      // all other questions
      return {
        ...question,
        sectionTotal: newSectionTotal
      };
    }
  });

  clonedAssessment.questions = editedQuestions;
  return clonedAssessment;
}

function InitializationPrerequisites({ children }) {
  const { urlSlug, appState, coreUrlParam, eliUrlParam } = useUrlSlugContext();
  const {
    dbSlugs,
    eli,
    coreUrl,
    eliUrl,
    theme,
    isSequenceResultsScreen,
    isSequence,
    isUni,
    isUni1,
    isUni2,
    isUni3,
    isUni4
  } = useAppConfig({ urlSlug, coreUrlParam, eliUrlParam });
  const [answers, setAnswers] = useState(
    appState?.answers ? appState.answers : []
  );
  const [profile, setProfile] = useState(() => {
    return appState?.profile
      ? appState.profile
      : getBlankProfile(isSequence, eli, isUni, isUni3 || isUni4);
  });
  const [authData, setAuthData] = useState(() => {
    return appState?.authData ? appState.authData : getBlankAuthData();
  });
  const [forceValidation, setForceValidation] = useState(false);
  const [contact, setContact] = useState(
    appState?.contact ? appState.contact : BLANK_CONTACT
  );
  const [nextStack, setNextStack] = useState(
    appState?.nextStack ? appState.nextStack : []
  );
  const loadedZendesk = useRef(false);

  useEffect(() => {
    let color = "#f4f5f9";

    if (isSequence) {
      color = "#F5F7FA";
    }
    document
      .querySelector('meta[name*="theme-color"]')
      .setAttribute("content", color);
  }, [isSequence]);

  useEffect(() => {
    if (!theme) {
      return;
    }

    // For theme refresh class will be removed later. It's needed for content switch on the release.
    let themeClass = `theme-${theme}`;
    if (theme !== "refresh") {
      themeClass = `theme${capitalize(theme)}`;
    }
    document.querySelector("body").classList.add(themeClass);
  }, [theme]);

  const { isLoading, data: assessment, isError } = useAssessments(dbSlugs);

  const { getLoggedInData, getLoggedInDataIsLoading } = useLoggedInDataHandler({
    authData,
    setAuthData,
    profile,
    setProfile
  });

  useEffect(() => {
    if (isSequence) {
      if (!loadedZendesk.current) {
        loadedZendesk.current = true;
        addZendeskScript();
      }
      getLoggedInData();
      setPageData({
        site_section: "clinical"
      });
    }
    if (isUni3 || isUni4) {
      getLoggedInData();
    }
  }, [isSequence]);

  useEffect(() => {
    if (forceValidation) {
      setForceValidation(false);
    }
  }, [forceValidation]);

  useEffect(() => {
    setPageData({
      assessment_type: urlSlug
    });
  }, [urlSlug]);

  if (isLoading || (isSequence && getLoggedInDataIsLoading)) {
    return "";
  }

  if (isError) {
    return "Error!";
  }

  if (!isValidAssessment(assessment)) {
    return "Error! Either question path or next question is not set.";
  }

  crmInit();

  let clonedAssessment = cloneDeep(assessment);
  if (eli) {
    clonedAssessment = updateQuestionsForEligibility(clonedAssessment);
  }

  const context = {
    assessment: clonedAssessment,
    answers,
    setAnswers,
    authData,
    setAuthData,
    forceValidation,
    setForceValidation,
    profile,
    setProfile,
    contact,
    setContact,
    nextStack,
    setNextStack,
    theme,
    newQuiz: {
      isUni,
      isUni1,
      isUni2,
      isUni3,
      isUni4,
      eli,
      isSequenceResultsScreen,
      coreUrl,
      eliUrl,
      dbSlugs: dbSlugs.join(),
      isAndroid: checkIfAndroid(),
      isIphone: checkIfIphone(),
      isSequence
    }
  };

  return (
    <>
      {isUniTestUrl(urlSlug) ? (
        <Experiment name={t("ONE_WW_UNIFIED_QUIZ_AB_TEST_NAME")}>
          <AppProvider value={context}>{children}</AppProvider>
        </Experiment>
      ) : (
        <AppProvider value={context}>{children}</AppProvider>
      )}
    </>
  );
}

const WaitForXsReadiness = ({ children }) => {
  const { isReady } = useXS(t("QUIZ_AB_TEST_NAME"));

  if (!isReady) {
    return null;
  }

  // Render the app only when XS is ready.

  return <InitializationPrerequisites>{children}</InitializationPrerequisites>;
};

export default WaitForXsReadiness;
